import React, { useState, useEffect, useRef } from 'react';
import { walletAmount, WalletValue, useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Zap, ArrowUpFromLine, Skull, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useChatLayout } from '../context/ChatLayoutContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// ==========================================
// K, M, B FORMATLAYICILARI
// ==========================================
const parseAmount = (valStr) => {
  if (!valStr) return 0;
  // ⚠️ BİNLİK AYIRICI TEMİZLENMELİ. formatAmount() büyük sayıları
  // toLocaleString ile üretiyor ("1,457,545"); parseFloat virgülde durup
  // 1 döndürüyordu. MAX butonuna basan kullanıcı 1 birim bahis oynuyordu.
  let str = valStr.toString().trim().toLowerCase().replace(/[,\s]/g, '');
  let multiplier = 1;
  if (str.endsWith('k')) { multiplier = 1000; str = str.slice(0, -1); }
  else if (str.endsWith('m')) { multiplier = 1000000; str = str.slice(0, -1); }
  else if (str.endsWith('b')) { multiplier = 1000000000; str = str.slice(0, -1); }
  const parsedValue = parseFloat(str);
  return isNaN(parsedValue) ? 0 : Math.floor(parsedValue * multiplier);
};

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '';
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

const DIFFICULTY_SETTINGS = {
  easy:      { steps: 24, label: 'Easy',      color: 'emerald', step_multiplier: 1.0444, maxLabel: '2.84x' },
  medium:    { steps: 22, label: 'Medium',    color: 'blue',    step_multiplier: 1.175,  maxLabel: '34.74x' },
  hard:      { steps: 20, label: 'Hard',      color: 'orange',  step_multiplier: 1.4462, maxLabel: '1605x' },
  daredevil: { steps: 10, label: 'Daredevil', color: 'rose',    step_multiplier: 2.0889, maxLabel: '1580x' },
};

// ==========================================
// ✅ DÜZELTME: Arabalar GERÇEK aspect ratio'larıyla
// (Yüklenen görsellerden ölçülen oranlar)
// ==========================================
const CARS = [
  { src: '/police_car.png',  aspectRatio: 1.00 },  // 500x500
  { src: '/red_truck.png',   aspectRatio: 1.23 },  // 430x350
  { src: '/white_car.png',   aspectRatio: 0.88 },  // 247x280
  { src: '/white_truck.png', aspectRatio: 1.23 },  // 430x350
];
const randomCar = () => CARS[Math.floor(Math.random() * CARS.length)];

// ==========================================
// CHICKEN SPRITE (288x96 = 3 frame x 96)
// ==========================================
const ChickenSprite = ({ frame, size }) => (
  <div
    style={{
      width: size,
      height: size,
      backgroundImage: 'url(/chicken.png)',
      backgroundSize: '300% 100%',
      backgroundPosition: `${frame * 50}% 0%`,
      backgroundRepeat: 'no-repeat',
      imageRendering: 'pixelated',
    }}
  />
);

export const ChickenRoad = () => {
  const { user, token, isAuthenticated, balance, fmtMoney, wallet, betToRaw, rawToBet, betLimits } = useAuth();

  const [betAmount, setBetAmount] = useState('');
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const [difficulty, setDifficulty] = useState('easy');

  const [phase, setPhase] = useState('idle');
  const [currentPosition, setCurrentPosition] = useState(0);
  const [currentMultiplier, setCurrentMultiplier] = useState(1.00);
  const [nextMultiplier, setNextMultiplier] = useState(null);
  const [roadMap, setRoadMap] = useState([]);
  const [serverSeedHash, setServerSeedHash] = useState('');
  const [serverSeed, setServerSeed] = useState('');
  const [winAmount, setWinAmount] = useState(0);
  const [crashedLaneIndex, setCrashedLaneIndex] = useState(null);
  const [crashCarData, setCrashCarData] = useState(CARS[0]);

  const [chickenFrame, setChickenFrame] = useState(0);
  const [isJumping, setIsJumping] = useState(false);
  const [atmosphereCars, setAtmosphereCars] = useState([]);

  const [userOffset, setUserOffset] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({ startX: 0, startOffset: 0, hasMoved: false });
  const autoReturnTimerRef = useRef(null);

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  const explosionSound = useRef(null);
  const successSound = useRef(null);
  const atmosphereTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const settings = DIFFICULTY_SETTINGS[difficulty];
  const maxSteps = settings.steps;

  const isMobile = containerSize.width < 640;
  const isTablet = containerSize.width >= 640 && containerSize.width < 1024;

  // ✅ MOBİLE OPTİMİZASYON: Daha küçük lane'ler ve elementler (dar viewport'a sığsın)
  const LANE_WIDTH    = isMobile ? 75  : isTablet ? 135 : 165;
  const SCENARY_WIDTH = isMobile ? 80  : isTablet ? 170 : 220;
  const CHICKEN_SIZE  = isMobile ? 42  : isTablet ? 78  : 92;
  const MANHOLE_SIZE  = isMobile ? 38  : isTablet ? 66  : 80;
  const CAR_WIDTH     = LANE_WIDTH * 1.15;

  // ✅ Barricade ile tavuk arası boşluk (mobile'da daha az boşluk - viewport dar)
  const BARRICADE_GAP = isMobile ? 4 : 8;

  // ✅ Yol oyun çerçevesinin tam baştan sonuna kadar (boşluk yok)
  const ROAD_TOP      = 0;
  const ROAD_BOTTOM   = containerSize.height;
  const ROAD_CENTER_Y = (ROAD_TOP + ROAD_BOTTOM) / 2;

  // ==========================================
  // ✅ DÜZELTME: Chicken pozisyon mantığı
  // currentPosition === 0 (idle veya henüz hiç adım atılmamış) → SOL SCENARY içi
  // currentPosition > 0 → lane (currentPosition - 1)'de (basılan son lane)
  // ==========================================
  const chickenWorldX = currentPosition === 0
    ? SCENARY_WIDTH * 0.5
    : SCENARY_WIDTH + (currentPosition - 1) * LANE_WIDTH + LANE_WIDTH / 2;
  const chickenWorldY = ROAD_CENTER_Y;

  const VIEWPORT_CHICKEN_RATIO = isMobile ? 0.22 : 0.32;
  const autoCameraOffsetX = Math.max(0, chickenWorldX - containerSize.width * VIEWPORT_CHICKEN_RATIO);
  const cameraOffsetX = userOffset !== null ? userOffset : autoCameraOffsetX;

  const worldWidth = SCENARY_WIDTH + maxSteps * LANE_WIDTH + SCENARY_WIDTH;
  const maxScrollOffset = Math.max(0, worldWidth - containerSize.width);

  // ==========================================
  // LIFECYCLE
  // ==========================================
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof Audio !== "undefined") {
      explosionSound.current = new Audio('/chickenDie.mp3');
      successSound.current   = new Audio('/chickend_success.mp3');
      explosionSound.current.volume = 0.7;
      successSound.current.volume   = 0.55;
    }
  }, []);

  const playSound = (audioRef) => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  };

  // ==========================================
  // ✅ DÜZELTME: Chicken sprite — sadece ZIPLARKEN yürüme animasyonu
  // idle/durağan'da frame 0 (durağan)
  // Frame değişim aralığı 80ms (daha akıcı yürüme)
  // ==========================================
  useEffect(() => {
    if (!isJumping) {
      setChickenFrame(0);
      return;
    }
    const interval = setInterval(() => {
      setChickenFrame(prev => (prev + 1) % 3);
    }, 80);
    return () => clearInterval(interval);
  }, [isJumping]);

  // ==========================================
  // ✅ DÜZELTME: Atmosfer arabaları HER ZAMAN aktif (oyun durumundan bağımsız)
  // - idle, playing, crashed, cashed_out → hepsinde arabalar geçer (oyun yaşıyor hissi)
  // - Tavuğun bulunduğu lane'e araba inmez (eziyormuş gibi olur)
  // - Sıradaki manhole'a (current) araba inmez (kullanıcı tıklayacak)
  // - Crash arabasının olduğu lane'e atmosfer arabası eklenmez (çakışma olur)
  // - Daha sık: 400-1200ms aralıkla, aynı anda max 3 araba
  // ==========================================
  useEffect(() => {
    const spawn = () => {
      if (!isMountedRef.current) return;
      if (document.visibilityState !== 'visible') return;

      setAtmosphereCars(prev => {
        if (prev.length >= 3) return prev;

        // Tavuk hangi lane'de?
        const chickenLane = currentPosition === 0 ? -1 : currentPosition - 1;
        // Sıradaki tıklanabilir manhole
        const nextLane = phase === 'playing' ? currentPosition : -1;
        // Crash arabasının olduğu lane
        const crashLane = crashedLaneIndex !== null ? crashedLaneIndex : -1;

        const available = [];
        for (let i = 0; i < maxSteps; i++) {
          if (i === chickenLane) continue;
          if (i === nextLane) continue;
          if (i === crashLane) continue;
          if (!prev.some(c => c.lane === i)) available.push(i);
        }
        if (available.length === 0) return prev;

        const lane = available[Math.floor(Math.random() * available.length)];
        const duration = 1.2 + Math.random() * 1.3;
        const id = `${Date.now()}-${Math.random()}`;
        const car = { id, lane, carData: randomCar(), duration };

        setTimeout(() => {
          if (!isMountedRef.current) return;
          setAtmosphereCars(cars => cars.filter(c => c.id !== id));
        }, duration * 1000 + 80);

        return [...prev, car];
      });
    };

    // Açılışta neredeyse anında ilk araba (oyun canlı görünsün)
    const initialDelay = setTimeout(spawn, 150);

    const tick = () => {
      const delay = 400 + Math.random() * 800; // Daha sık: 400-1200ms
      atmosphereTimerRef.current = setTimeout(() => {
        spawn();
        tick();
      }, delay);
    };
    tick();

    return () => {
      clearTimeout(initialDelay);
      if (atmosphereTimerRef.current) clearTimeout(atmosphereTimerRef.current);
    };
  }, [phase, currentPosition, maxSteps, crashedLaneIndex]);

  // ==========================================
  // KAMERA PAN — Pointer Drag
  // ==========================================
  const handlePointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (autoReturnTimerRef.current) {
      clearTimeout(autoReturnTimerRef.current);
      autoReturnTimerRef.current = null;
    }
    setIsDragging(true);
    dragStateRef.current = {
      startX: e.clientX,
      startOffset: userOffset !== null ? userOffset : autoCameraOffsetX,
      hasMoved: false
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const dx = e.clientX - dragStateRef.current.startX;
    if (Math.abs(dx) > 3) dragStateRef.current.hasMoved = true;
    const newOffset = Math.max(
      0,
      Math.min(maxScrollOffset, dragStateRef.current.startOffset - dx)
    );
    setUserOffset(newOffset);
  };

  const handlePointerUp = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    if (dragStateRef.current.hasMoved) {
      autoReturnTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        setUserOffset(null);
      }, 2500);
    } else {
      setUserOffset(null);
    }
  };

  useEffect(() => {
    if (autoReturnTimerRef.current) {
      clearTimeout(autoReturnTimerRef.current);
      autoReturnTimerRef.current = null;
    }
    setUserOffset(null);
  }, [currentPosition, phase, difficulty]);

  useEffect(() => () => {
    if (autoReturnTimerRef.current) clearTimeout(autoReturnTimerRef.current);
  }, []);

  // ==========================================
  // BET INPUT HELPERS
  // ==========================================
  const handleBetChange = (val) => setBetAmount(val);
  const addBetAmount = (val) => setBetAmount(String(parseAmount(betAmount) + val));
  const multiplyBet = (mult) => setBetAmount(String(Math.floor(parseAmount(betAmount) * mult)));

  // ==========================================
  // OYUNU BAŞLAT
  // ==========================================
  const startGame = async () => {
// 🎯 Kullanıcı AKTİF CÜZDANIN biriminde yazar; betToRaw saklama
    // birimine çevirir (BB'de ×1000).
    const amount = betToRaw(parseAmount(betAmount));
    if (!isAuthenticated) return toast.error("Please log in first!");
    // ⚠️ Limitler cüzdana göre (site birimi gömülüydü).
    if (!amount || amount < betLimits.min_bet || amount > betLimits.max_bet)
      return toast.error(`Bet must be between ${walletAmount(betLimits.min_bet, wallet)} and ${walletAmount(betLimits.max_bet, wallet)}.`);

    try {
      const response = await axios.post(`${API}/game/chicken-road/start`, {
        amount: amount,
        difficulty: difficulty
      }, { headers: { Authorization: `Bearer ${token}` } });

      setPhase('playing');
      setCurrentPosition(0);
      setCurrentMultiplier(1.00);
      setNextMultiplier(response.data.next_multiplier);
      setRoadMap([]);
      setServerSeed('');
      setServerSeedHash(response.data.server_seed_hash);
      setWinAmount(0);
      setCrashedLaneIndex(null);
      setAtmosphereCars([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start game!");
    }
  };

  // ==========================================
  // ADIM AT
  // ==========================================
  const handleStep = async () => {
    if (phase !== 'playing' || isJumping) return;
    const stepIndex = currentPosition;
    setIsJumping(true);

    try {
      const [data] = await Promise.all([
        axios.post(`${API}/game/chicken-road/step`, { step_index: stepIndex }, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.data),
        new Promise(resolve => setTimeout(resolve, 400))
      ]);

      if (!isMountedRef.current) return;
      setIsJumping(false);

      if (data.status === 'playing') {
        setCurrentPosition(prev => prev + 1);
        setCurrentMultiplier(data.multiplier);
        setNextMultiplier(data.next_multiplier);
        playSound(successSound); // ✅ Her güvenli adımda kazanma sesi
      } else if (data.status === 'crashed') {
        setCrashCarData(randomCar());
        setCrashedLaneIndex(stepIndex);
        setCurrentPosition(prev => prev + 1);
        setRoadMap(data.road_map);
        setServerSeed(data.server_seed);
        setPhase('crashed');
        setTimeout(() => playSound(explosionSound), 220);
      } else if (data.status === 'cashed_out') {
        setCurrentPosition(prev => prev + 1);
        setCurrentMultiplier(data.multiplier);
        setWinAmount(data.win_amount);
        setRoadMap(data.road_map);
        setServerSeed(data.server_seed);
        setPhase('cashed_out');
        playSound(successSound);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      setIsJumping(false);
      toast.error(error.response?.data?.detail || "Error stepping!");
    }
  };

  // ==========================================
  // CASHOUT
  // ==========================================
  const handleCashout = async () => {
    if (phase !== 'playing' || currentPosition === 0 || isJumping) return;
    try {
      const response = await axios.post(`${API}/game/chicken-road/cashout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setPhase('cashed_out');
      setWinAmount(data.win_amount);
      setCurrentMultiplier(data.multiplier);
      setRoadMap(data.road_map);
      setServerSeed(data.server_seed);
      playSound(successSound);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error cashing out!");
    }
  };

  // Backend ile birebir aynı multiplier hesaplama
  const getMultiplierAtLane = (laneIdx) =>
    Math.pow(DIFFICULTY_SETTINGS[difficulty].step_multiplier, laneIdx + 1);

  const difficultyButtonClass = (key) => {
    const isActive = difficulty === key;
    if (!isActive) {
      return 'bg-[#2A2D31]/50 text-zinc-400 hover:bg-[#33363b] hover:text-white';
    }
    const colorMap = {
      emerald: 'bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.5)]',
      blue:    'bg-blue-500    text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]',
      orange:  'bg-orange-500  text-white shadow-[0_0_15px_rgba(249,115,22,0.5)]',
      rose:    'bg-rose-500    text-white shadow-[0_0_15px_rgba(244,63,94,0.5)]',
    };
    return colorMap[DIFFICULTY_SETTINGS[key].color] || 'bg-[#B059E8] text-white';
  };

  const difficultyBadgeClass = () => {
    const colorMap = {
      emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
      blue:    'bg-blue-500/20    text-blue-300    border-blue-500/50',
      orange:  'bg-orange-500/20  text-orange-300  border-orange-500/50',
      rose:    'bg-rose-500/20    text-rose-300    border-rose-500/50',
    };
    return colorMap[settings.color];
  };

  return (
    <div className="min-h-screen bg-[#0E0F11]" data-testid="chicken-road-page">
      <div className="max-w-[1800px] mx-auto px-2 sm:px-4 pt-20 md:pt-24 pb-4 md:pb-8">
        <div className={`flex flex-col lg:flex-row gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>

          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          <main className="flex-1 flex flex-col lg:flex-row gap-6 min-w-0 lg:h-[calc(100vh-120px)]">

            {/* KONTROLLER */}
            <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-4 lg:h-full">
              <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 shadow-lg flex flex-col lg:h-full">
                <div className="space-y-6 flex-1">
                  <div>
                    <label className="flex items-center gap-2 text-xs text-zinc-400 font-bold mb-2">
                      <Zap className="w-4 h-4" /> DIFFICULTY
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(DIFFICULTY_SETTINGS).map(([key, s]) => (
                        <button
                          key={key}
                          onClick={() => setDifficulty(key)}
                          disabled={phase === 'playing'}
                          className={`py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-50 ${difficultyButtonClass(key)}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 flex justify-between items-center text-[10px] text-zinc-500">
                      <span>{settings.steps} lanes</span>
                      <span>Max win: <span className="text-zinc-300">{settings.maxLabel}</span></span>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-zinc-400 font-bold mb-1.5 block">Bet Amount</label>
                    <div className="flex bg-[#15171a] border border-[#2A2D31] rounded-xl overflow-hidden focus-within:border-[#B059E8] transition-colors">
                      <div className="pl-3 flex items-center justify-center">
                        <span className="text-zinc-500 font-bold">$</span>
                      </div>
                      <input
                        type="text"
                        value={betAmount}
                        onChange={(e) => handleBetChange(e.target.value)}
                        disabled={phase === 'playing'}
                        className="w-full bg-transparent py-3 px-2 text-white font-bold outline-none disabled:opacity-50 uppercase"
                        placeholder="1"
                      />
                      <div className="flex border-l border-[#2A2D31]">
                        <button onClick={() => addBetAmount(10)} disabled={phase === 'playing'} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">+10</button>
                        <button onClick={() => multiplyBet(0.5)} disabled={phase === 'playing'} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">1/2</button>
                        <button onClick={() => multiplyBet(2)} disabled={phase === 'playing'} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors">2x</button>
                      </div>
                    </div>
                  </div>

                  {phase === 'playing' && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleStep}
                      disabled={isJumping}
                      className="w-full py-4 rounded-xl font-heading font-bold text-base lg:text-lg uppercase tracking-wider transition-all shadow-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white disabled:opacity-50 disabled:grayscale flex items-center justify-center gap-2"
                    >
                      <ArrowUpFromLine className="w-5 h-5" />
                      Step Forward
                    </motion.button>
                  )}
                </div>

                <div className="mt-auto pt-6">
                  {phase === 'idle' || phase === 'crashed' || phase === 'cashed_out' ? (
                    <button
                      onClick={startGame}
                      disabled={!betAmount}
                      className="w-full py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-all shadow-lg bg-gradient-to-r from-[#7b2fc4] to-[#B059E8] hover:from-[#B059E8] hover:to-[#c98bf0] text-white disabled:opacity-50 disabled:grayscale"
                    >
                      {(phase === 'crashed' || phase === 'cashed_out') ? 'Play Again' : 'Bet'}
                    </button>
                  ) : (
                    <button
                      onClick={handleCashout}
                      disabled={currentPosition === 0 || isJumping}
                      className="w-full py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-all shadow-lg bg-gradient-to-r from-[#16A34A] to-[#2ecf6e] hover:from-[#2ecf6e] hover:to-[#5ee08f] text-white disabled:opacity-50 disabled:grayscale flex flex-col items-center justify-center leading-none"
                    >
                      <span className="mb-1">CASHOUT</span>
                      {currentPosition > 0 && (
                        <span className="text-sm opacity-90 mm-num">
                          <WalletValue amount={Math.floor(betToRaw(parseAmount(betAmount)) * currentMultiplier)} wallet={wallet} />
                        </span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* OYUN ALANI */}
            <div className="flex-1 flex flex-col bg-[#1A1D20] rounded-2xl border border-[#2A2D31] shadow-2xl relative overflow-hidden h-[70vh] min-h-[480px] md:h-[65vh] lg:h-full">

              {/* MULTIPLIER (top-left) */}
              <div className="absolute top-2 left-2 md:top-5 md:left-5 z-30 flex flex-col pointer-events-none bg-[#15171a]/60 md:bg-transparent backdrop-blur-sm md:backdrop-blur-0 rounded-lg px-2 py-1 md:px-0 md:py-0">
                <span className="text-zinc-400 text-[9px] md:text-xs font-bold uppercase tracking-widest mb-0.5">Current Multiplier</span>
                <span
                  className={`text-2xl md:text-5xl font-heading font-black ${
                    phase === 'crashed' ? 'text-rose-500' :
                    phase === 'cashed_out' ? 'text-[#2ecf6e]' :
                    'text-white'
                  } drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] leading-none`}
                >
                  {currentMultiplier.toFixed(2)}x
                </span>
                {phase === 'playing' && nextMultiplier && (
                  <span className="text-zinc-300 mm-num text-[9px] md:text-xs mt-0.5 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
                    Next: <span className="text-amber-300 font-bold">{nextMultiplier.toFixed(2)}x</span>
                  </span>
                )}
              </div>

              {/* DIFFICULTY BADGE (top-right) */}
              <div className="absolute top-2 right-2 md:top-5 md:right-5 z-30 pointer-events-none">
                <span className={`px-2 py-1 md:px-3 md:py-1.5 rounded-lg text-[9px] md:text-xs font-black uppercase tracking-wider border backdrop-blur-sm ${difficultyBadgeClass()}`}>
                  {settings.label} · {maxSteps}L
                </span>
              </div>

              {/* DRAG/PAN CONTAINER */}
              <div
                ref={containerRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                className="absolute inset-0 overflow-hidden select-none"
                style={{
                  cursor: isDragging ? 'grabbing' : 'grab',
                  touchAction: 'pan-y'
                }}
              >
                <motion.div
                  initial={false}
                  animate={{ x: -cameraOffsetX }}
                  transition={isDragging ? { duration: 0 } : { duration: 0.55, ease: [0.32, 0.72, 0.35, 1] }}
                  style={{
                    width: worldWidth || '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    willChange: 'transform'
                  }}
                >

                  {/* GÖKYÜZÜ */}
                  <div
                    className="absolute left-0 right-0"
                    style={{
                      top: 0,
                      height: ROAD_TOP,
                      background: 'linear-gradient(180deg, #0d1f35 0%, #1a3050 60%, #2d4360 100%)'
                    }}
                  />

                  {/* ALT ZEMİN */}
                  <div
                    className="absolute left-0 right-0"
                    style={{
                      top: ROAD_BOTTOM,
                      bottom: 0,
                      background: 'linear-gradient(0deg, #0d1f35 0%, #1a3050 60%, #2d4360 100%)'
                    }}
                  />

                  {/* ASFALT YOL */}
                  <div
                    className="absolute left-0 right-0"
                    style={{
                      top: ROAD_TOP,
                      height: ROAD_BOTTOM - ROAD_TOP,
                      background: 'linear-gradient(180deg, #2a323d 0%, #353e4a 6%, #3a4451 50%, #353e4a 94%, #2a323d 100%)',
                      boxShadow: 'inset 0 8px 16px rgba(0,0,0,0.45), inset 0 -8px 16px rgba(0,0,0,0.45)'
                    }}
                  />

                  {/* ÜST/ALT SARI ŞERİT */}
                  <div className="absolute left-0 right-0" style={{ top: ROAD_TOP - 2, height: 4, background: 'linear-gradient(180deg, #fde047, #facc15, #ca8a04)', boxShadow: '0 0 12px rgba(253,224,71,0.4)' }} />
                  <div className="absolute left-0 right-0" style={{ top: ROAD_BOTTOM - 2, height: 4, background: 'linear-gradient(180deg, #ca8a04, #facc15, #fde047)', boxShadow: '0 0 12px rgba(253,224,71,0.4)' }} />

                  {/* SOL DEKOR */}
                  <img
                    src="/left_scenary.png"
                    alt=""
                    draggable="false"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: SCENARY_WIDTH,
                      height: containerSize.height || '100%',
                      objectFit: 'cover',
                      imageRendering: 'pixelated',
                      zIndex: 12,
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  />

                  {/* ŞERİTLER */}
                  {containerSize.width > 0 && Array.from({ length: maxSteps }).map((_, idx) => {
                    const laneX = SCENARY_WIDTH + idx * LANE_WIDTH;
                    // ✅ DÜZELTME: Doğru mantık
                    // - isPast: tavuk basıldıktan sonra geride bırakılan lane'ler (barricade)
                    // - isCurrent: tıklanabilir sıradaki manhole
                    // - isCrashedHere: crash arabası bu lane'de
                    const isCrashedHere = phase === 'crashed' && idx === crashedLaneIndex;
                    const isPast = idx < currentPosition && !isCrashedHere;
                    const isCurrent = idx === currentPosition && phase === 'playing';
                    const isClickable = isCurrent && !isJumping;
                    const laneMultiplier = getMultiplierAtLane(idx);

                    return (
                      <div
                        key={idx}
                        style={{
                          position: 'absolute',
                          left: laneX,
                          top: 0,
                          width: LANE_WIDTH,
                          height: containerSize.height,
                          pointerEvents: 'none'
                        }}
                      >
                        {/* Şerit çizgisi */}
                        {idx > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              left: -1,
                              top: ROAD_TOP + 8,
                              height: ROAD_BOTTOM - ROAD_TOP - 16,
                              width: 3,
                              backgroundImage: 'repeating-linear-gradient(180deg, rgba(255,255,255,0.85) 0 18px, transparent 18px 34px)',
                              borderRadius: 2
                            }}
                          />
                        )}

                        {/* MANHOLE / BARRICADE — Crashed lane'de hiçbir şey (sadece crash arabası gelecek) */}
                        {!isCrashedHere && (
                          isPast ? (
                            // ✅ BARRICADE — Yolun üst kısmında, tavuğun YUKARISINDA (üzerinde değil)
                            <motion.img
                              src="/barricade.png"
                              alt=""
                              draggable="false"
                              initial={{ scale: 0, rotate: -15 }}
                              animate={{ scale: [0, 1.2, 0.95, 1], rotate: 0 }}
                              transition={{ duration: 0.35, times: [0, 0.5, 0.8, 1], ease: 'easeOut' }}
                              style={{
                                position: 'absolute',
                                left: LANE_WIDTH / 2 - MANHOLE_SIZE / 2,
                                top: ROAD_CENTER_Y - CHICKEN_SIZE / 2 - MANHOLE_SIZE - BARRICADE_GAP, // ✅ Tavuğun yukarısında (mobile için BARRICADE_GAP responsive)
                                width: MANHOLE_SIZE,
                                height: 'auto',
                                imageRendering: 'pixelated',
                                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))',
                                userSelect: 'none',
                                zIndex: 11
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                position: 'absolute',
                                left: LANE_WIDTH / 2 - MANHOLE_SIZE / 2,
                                top: ROAD_CENTER_Y - MANHOLE_SIZE / 2,
                                width: MANHOLE_SIZE,
                                height: MANHOLE_SIZE,
                                zIndex: 5
                              }}
                            >
                              <motion.div
                                onClick={isClickable ? handleStep : undefined}
                                onPointerDown={isClickable ? (e) => e.stopPropagation() : undefined}
                                animate={isClickable ? { scale: [1, 1.08, 1] } : { scale: 1 }}
                                transition={isClickable
                                  ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' }
                                  : { duration: 0.3 }
                                }
                                whileHover={isClickable ? { scale: 1.18 } : {}}
                                whileTap={isClickable ? { scale: 0.92 } : {}}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'relative',
                                  pointerEvents: isClickable ? 'auto' : 'none',
                                  cursor: isClickable ? 'pointer' : 'default',
                                  filter: isClickable
                                    ? 'drop-shadow(0 0 14px rgba(251,191,36,0.65))'
                                    : 'drop-shadow(0 3px 4px rgba(0,0,0,0.5))'
                                }}
                              >
                                <img
                                  src="/manhole.png"
                                  alt=""
                                  draggable="false"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    imageRendering: 'pixelated',
                                    userSelect: 'none',
                                    pointerEvents: 'none'
                                  }}
                                />
                                <div
                                  className="absolute inset-0 flex items-center justify-center mm-num font-black select-none"
                                  style={{
                                    fontSize: MANHOLE_SIZE * 0.22,
                                    color: '#fff',
                                    textShadow: '0 0 4px rgba(0,0,0,1), 0 2px 3px rgba(0,0,0,0.9)',
                                    letterSpacing: '-0.5px',
                                    pointerEvents: 'none'
                                  }}
                                >
                                  {laneMultiplier.toFixed(2)}x
                                </div>
                              </motion.div>
                            </div>
                          )
                        )}

                      </div>
                    );
                  })}

                  {/* ATMOSFER ARABALARI — ✅ ortalanmış, aspect ratio doğru */}
                  <AnimatePresence>
                    {atmosphereCars.map(car => {
                      // ✅ DÜZELTME: left = laneX - CAR_WIDTH/2 (ortalama), translateX KULLANMA
                      // Çünkü Framer Motion animate y, transform'u override ediyor
                      const carLeft = SCENARY_WIDTH + car.lane * LANE_WIDTH + LANE_WIDTH / 2 - CAR_WIDTH / 2;
                      return (
                        <motion.img
                          key={car.id}
                          src={car.carData.src}
                          alt=""
                          draggable="false"
                          initial={{ y: -300, opacity: 0, rotate: 180 }}
                          animate={{
                            y: (containerSize.height || 600) + 100,
                            opacity: [0, 1, 1, 1, 0.8],
                            rotate: 180
                          }}
                          transition={{
                            duration: car.duration,
                            ease: [0.55, 0.0, 0.7, 0.1],
                            opacity: { duration: car.duration, times: [0, 0.08, 0.4, 0.85, 1] }
                          }}
                          style={{
                            position: 'absolute',
                            left: carLeft,
                            top: 0,
                            width: CAR_WIDTH,
                            aspectRatio: car.carData.aspectRatio,
                            height: 'auto',
                            zIndex: 8,
                            pointerEvents: 'none',
                            willChange: 'transform',
                            userSelect: 'none'
                          }}
                        />
                      );
                    })}
                  </AnimatePresence>

                  {/* CRASH ARABASI — ✅ aspect ratio + ortalama */}
                  {phase === 'crashed' && crashedLaneIndex !== null && containerSize.height > 0 && (
                    <motion.img
                      src={crashCarData.src}
                      alt=""
                      draggable="false"
                      initial={{ y: -350, opacity: 0, rotate: 180 }}
                      animate={{
                        y: chickenWorldY - (CAR_WIDTH / crashCarData.aspectRatio) * 0.4,
                        opacity: 1,
                        rotate: 180
                      }}
                      transition={{
                        duration: 0.4,
                        ease: [0.55, 0.0, 0.7, 0.1],
                        opacity: { duration: 0.1 }
                      }}
                      style={{
                        position: 'absolute',
                        left: SCENARY_WIDTH + crashedLaneIndex * LANE_WIDTH + LANE_WIDTH / 2 - CAR_WIDTH / 2,
                        top: 0,
                        width: CAR_WIDTH,
                        aspectRatio: crashCarData.aspectRatio,
                        height: 'auto',
                        zIndex: 22,
                        pointerEvents: 'none',
                        filter: 'drop-shadow(0 8px 12px rgba(0,0,0,0.6))',
                        userSelect: 'none'
                      }}
                    />
                  )}

                  {/* TAVUK */}
                  {containerSize.height > 0 && (
                    <motion.div
                      initial={false}
                      animate={{ x: chickenWorldX }}
                      transition={{ duration: 0.4, ease: 'linear' }}
                      style={{
                        position: 'absolute',
                        top: chickenWorldY,
                        left: -CHICKEN_SIZE / 2,
                        zIndex: 15,
                        pointerEvents: 'none',
                        willChange: 'transform'
                      }}
                    >
                      <motion.div
                        animate={isJumping
                          ? { y: [-CHICKEN_SIZE / 2, -CHICKEN_SIZE / 2 - (isMobile ? 40 : 65), -CHICKEN_SIZE / 2] }
                          : { y: -CHICKEN_SIZE / 2 }
                        }
                        transition={{
                          duration: 0.4,
                          times: [0, 0.5, 1],
                          ease: [0.45, 0, 0.55, 1]
                        }}
                      >
                        <motion.div
                          animate={{ opacity: phase === 'crashed' ? 0 : 1 }}
                          transition={{ delay: phase === 'crashed' ? 0.35 : 0, duration: 0.15 }}
                          style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.5))' }}
                        >
                          <ChickenSprite frame={chickenFrame} size={CHICKEN_SIZE} />
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  )}

                  {/* KAN EFEKTİ */}
                  {phase === 'crashed' && containerSize.height > 0 && (
                    <motion.img
                      src="/chicken_blood.png"
                      alt=""
                      draggable="false"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{
                        scale: [0, 0.4, 1.5, 1.15, 1.15],
                        opacity: [0, 0.6, 1, 1, 1]
                      }}
                      transition={{
                        duration: 0.28,
                        delay: 0.4,
                        times: [0, 0.18, 0.55, 0.82, 1],
                        ease: 'easeOut'
                      }}
                      style={{
                        position: 'absolute',
                        left: chickenWorldX - (CHICKEN_SIZE * 0.7) / 2,
                        top: chickenWorldY - (CHICKEN_SIZE * 0.7) / 2,
                        width: CHICKEN_SIZE * 0.7,
                        height: 'auto',
                        zIndex: 24,
                        pointerEvents: 'none',
                        imageRendering: 'pixelated',
                        filter: 'drop-shadow(0 0 10px rgba(180,0,0,0.5))',
                        userSelect: 'none'
                      }}
                    />
                  )}

                  {/* SAĞ DEKOR */}
                  <img
                    src="/right_scenary.png"
                    alt=""
                    draggable="false"
                    style={{
                      position: 'absolute',
                      left: SCENARY_WIDTH + maxSteps * LANE_WIDTH,
                      top: 0,
                      width: SCENARY_WIDTH,
                      height: containerSize.height || '100%',
                      objectFit: 'cover',
                      imageRendering: 'pixelated',
                      zIndex: 12,
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  />

                </motion.div>
              </div>

              {/* CRASHED / CASHED_OUT BANNER */}
              <AnimatePresence>
                {phase === 'crashed' && (
                  <motion.div
                    initial={{ y: 40, opacity: 0, scale: 0.7 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 40, opacity: 0 }}
                    transition={{ delay: 0.6, type: 'spring', stiffness: 200, damping: 14 }}
                    className="absolute bottom-12 md:bottom-16 left-1/2 -translate-x-1/2 bg-gradient-to-r from-rose-600 to-rose-500 text-white px-6 md:px-8 py-2.5 md:py-3 rounded-full font-bold text-base md:text-lg shadow-[0_0_30px_rgba(244,63,94,0.6)] z-30 flex items-center gap-2 pointer-events-none"
                  >
                    <Skull className="w-5 h-5" />
                    Splat!
                  </motion.div>
                )}
                {phase === 'cashed_out' && (
                  <motion.div
                    initial={{ y: 40, opacity: 0, scale: 0.7 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 14 }}
                    className="absolute bottom-12 md:bottom-16 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#16A34A] to-[#2ecf6e] text-white px-6 md:px-8 py-2.5 md:py-3 rounded-full font-bold text-base md:text-lg shadow-[0_0_30px_rgba(22,163,74,0.6)] flex items-center gap-2 z-30 pointer-events-none"
                  >
                    <ArrowUpFromLine className="w-5 h-5" />
                    Won <WalletValue amount={winAmount} wallet={wallet} />!
                  </motion.div>
                )}
              </AnimatePresence>

              {/* PROVABLY FAIR FOOTER */}
              <div className="absolute bottom-2 left-2 right-2 md:bottom-3 md:left-4 md:right-4 flex flex-col md:flex-row items-center justify-between gap-1 md:gap-2 text-[10px] md:text-xs text-zinc-500 mm-num pointer-events-none z-30">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
                  <span>Provably Fair</span>
                </div>
                {serverSeedHash && (
                  <div className="flex gap-3 md:gap-4 w-full md:w-auto overflow-hidden opacity-70">
                    <span className="truncate" title={serverSeedHash}>
                      <span className="text-zinc-600">Hash:</span> {serverSeedHash.substring(0, 16)}...
                    </span>
                    {serverSeed && (
                      <span className="truncate text-[#16A34A]" title={serverSeed}>
                        <span className="text-[#15913f]">Seed:</span> {serverSeed.substring(0, 16)}...
                      </span>
                    )}
                  </div>
                )}
              </div>

            </div>
          </main>
        </div>
      </div>
    </div>
  );
};